import React from 'react';
import { helpers } from '@simplybenefits/sb-utils';
import { Chip } from '@mui/material';
import { Text, Theme, i18n, tokens } from '@simplybenefits/sb-ui-library';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import CoverageBlock from '../../components/CoverageBlock.js';
import CoverageBlockMedical from '../../components/CoverageBlockMedical.js';
import CoverageBlockMedicalNoMax from '../../components/CoverageBlockMedicalNoMax.js';

import { numberWithCommas } from '../../services/helpers.js';

/**
 *
 * @param {*} props
 */
export default function MajorMedical({ classes, expanded, majorMedical, handleChange, defineCoverageType, showDependentWarning, waivedEHC }) {
    const privateHospitalType = majorMedical.privateHospital === 0 ? i18n.t(tokens.labels.coverage.semiPrivate) : i18n.t(tokens.labels.coverage.private);
    const combinedMaxString = majorMedical.isCombinedMaximum ? i18n.t(tokens.labels.coverage.combinedMaximumForMedicalService, { combinedMaximum: numberWithCommas(majorMedical.combinedMaximum) }) : "";

    return (
        <div style={ classes.block }>
            <Accordion
                square
                onChange={handleChange('majorMedical')}
                style={{ boxShadow: 'none' }}
                disableGutters={true}
            >
                <AccordionSummary
                    classes={{
                        content: classes.panelContent,
                        expanded: classes.expanded,
                        root: classes.root
                    }}
                    expandIcon={expanded.includes('majorMedical') ? <RemoveIcon /> : <AddIcon />}
                    sx={classes.accordionSummary}
                >
                    <Text medium bold>{ i18n.t(tokens.titles.majorMedical) }</Text>
                    {
                        waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "0 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                    }
                </AccordionSummary>
                <AccordionDetails style={classes.panelDetails}>
                    {
                        majorMedical.isEligible !== 1 ?
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.ineligibleForBenefit)}
                                </Text>
                            </div>
                            : 
                            null
                    }

                    {
                        showDependentWarning &&
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.waivedBenefitForDependents) }
                                </Text>
                            </div>
                    }
                    
                    {
                        majorMedical.reimbursement > 0 ?
                            <div style={classes.row}>
                                <Text small bold margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.reimbursementCoverageForMedicalService, { reimbursement: majorMedical.reimbursement, combinedMaxString, period: majorMedical.isCombinedMaximum ? "" : "." }) }
                                </Text>
                            </div>
                            :
                            null
                    }

                    {
                        majorMedical.hasDeductible ?
                            defineCoverageType(majorMedical)
                            : 
                            null
                    }

                    <React.Fragment>
                        <CoverageBlock
                            title={ i18n.t(tokens.labels.coverage.benefit) }
                            value={ i18n.t(tokens.labels.maximum) }
                            type="header"
                        />
                        <CoverageBlockMedical
                            coverage={majorMedical}
                        />
                    </React.Fragment>

                    <React.Fragment>
                        <CoverageBlock
                            title={ i18n.t(tokens.labels.coverage.benefit) }
                            value={ i18n.t(tokens.labels.coverage.reasonableAndCustomary) }
                            type="header"
                        />
                        <CoverageBlockMedicalNoMax
                            coverage={majorMedical}
                        />
                    </React.Fragment>

                    {
                        majorMedical.hearingAids !== 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.hearingAidsPerInsured, { hearingAids: numberWithCommas(majorMedical.hearingAids), hearingAidsPeriod: majorMedical.hearingAidsPeriod })}
                                </Text>
                            </div>
                            : 
                            null
                    }

                    {
                        majorMedical.includeConvalescentHospital !== 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    { i18n.t(
                                        tokens.labels.coverage.convalescentHospital, 
                                        { convalescentHospitalReimbursement: majorMedical.convalescentHospitalReimbursement, 
                                            convalescentHospitalDailyMax: majorMedical.convalescentHospitalDailyMax, 
                                            convalescentHospitalMaxDays: majorMedical.convalescentHospitalMaxDays }
                                    )}
                                </Text>
                            </div>
                            : null
                    }

                    {
                        majorMedical.cataractSurgeryEyewear !== 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.cataractSurgeryEyewear, { cataractSurgeryEyewear: numberWithCommas(majorMedical.cataractSurgeryEyewear), cataractSurgeryEyewearPeriod: majorMedical.cataractSurgeryEyewearPeriod })}
                                </Text>
                            </div>
                            : 
                            null
                    }

                    {
                        majorMedical.apneaMachine > 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.apneaMachineAmount, { apneaMachine: numberWithCommas(majorMedical.apneaMachine) })}
                                </Text>
                            </div>
                            : null
                    }

                    {
                        majorMedical.insulinPump > 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.insulinPumpAmount, { insulinPump: numberWithCommas(majorMedical.insulinPump) })}
                                </Text>
                            </div>
                            : null
                    }   

                    <div style={classes.row}>
                        <Text small margin="10px 0px 10px 0px">
                            { i18n.t(tokens.labels.coverage.externalBreastProsthesis) }
                        </Text>
                    </div>

                    <div style={classes.row}>
                        <Text small margin="10px 0px 10px 0px">
                            { i18n.t(tokens.labels.coverage.hospitalConfinement, { privateHospitalType }) }
                        </Text>
                    </div>

                    {
                        majorMedical.includeLaserEyeSurgery !== 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.laserEye, { laserEyeSurgeryReimbursement: helpers.formatNumber(majorMedical.laserEyeSurgeryReimbursement, 2, false), laserEyeSurgeryMaximum: helpers.formatNumber(majorMedical.laserEyeSurgeryMaximum, 2, true) }) }
                                </Text>
                            </div>
                            : 
                            null
                    }

                    {
                        majorMedical.includeGenderAffirmation !== 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    { i18n.t(tokens.labels.coverage.genderAffirmationCoverage, { genderAffirmationReimbursement: helpers.formatNumber(majorMedical.genderAffirmationReimbursement, 2, false), genderAffirmationMax: helpers.formatNumber(majorMedical.genderAffirmationMax, 2, true) }) }
                                </Text>
                            </div>
                            : 
                            null
                    }

                    <Text small bold margin="10px 0px 0px 0px">
                        { i18n.t(tokens.labels.coverage.majorMedicalDisclaimer) }
                    </Text>
                </AccordionDetails>
            </Accordion>
        </div>
    );
}